import React, { useState, useEffect } from 'react';
import { MapPin, Mail, Phone, Globe, Search, Filter, Users, Building, ChevronDown, ChevronUp, Music, Calendar, Tent, Lock, Sparkles } from 'lucide-react';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase/config';

const ALL_GENRES = [
  'rock', 'metal', 'punk', 'hardcore', 'indie',
  'pop', 'singer-songwriter', 'folk', 'country',
  'jazz', 'blues', 'soul', 'funk',
  'electronic', 'techno', 'house', 'dance',
  'hip-hop', 'r&b', 'urban',
  'classical', 'chamber', 'orchestra',
  'world', 'latin', 'reggae', 'ska',
  'experimental', 'avant-garde', 'noise',
  'acoustic', 'covers', 'tribute'
];

export default function VenuesTab({ user, bandId, isPremium }) {
  const [venues, setVenues] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [bandProfile, setBandProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [viewMode, setViewMode] = useState('venues');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [minCapacity, setMinCapacity] = useState('');
  const [maxCapacity, setMaxCapacity] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  const [showFilters, setShowFilters] = useState(false);
  const [genreMatchMode, setGenreMatchMode] = useState('any');

  const PREVIEW_COUNT = 3;

  const getPreviewItems = (items) => {
    if (items.length <= PREVIEW_COUNT) return items;
    const shuffled = [...items].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, PREVIEW_COUNT);
  };

  // ── Data listeners ────────────────────────────────────────
  useEffect(() => {
    if (!user || !bandId) return;
    const bandRef = ref(database, `bands/${bandId}`);
    const unsubscribe = onValue(bandRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setBandProfile({ location: data.location, region: data.region, genres: data.genres || [] });
      }
    });
    return () => unsubscribe();
  }, [user, bandId]);

  useEffect(() => {
    const venuesRef = ref(database, 'venues');
    const unsubscribe = onValue(venuesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setVenues(Object.keys(data).map(key => ({ id: key, type: 'venue', ...data[key] })));
      } else { setVenues([]); }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const festivalsRef = ref(database, 'festivals');
    const unsubscribe = onValue(festivalsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setFestivals(Object.keys(data).map(key => ({ id: key, type: 'festival', ...data[key] })));
      } else { setFestivals([]); }
    });
    return () => unsubscribe();
  }, []);

  // ── Helpers ───────────────────────────────────────────────
  const calculateGenreMatchScore = (item, targetGenres) => {
    if (!item.genres || item.genres.length === 0 || targetGenres.length === 0) return 0;
    return item.genres.filter(g => targetGenres.includes(g)).length;
  };

  const getSourceItems = (mode, v, f) => {
    if (mode === 'venues') return v;
    if (mode === 'festivals') return f;
    return [...v, ...f];
  };

  // ── Filtering ─────────────────────────────────────────────
  useEffect(() => {
    let filtered = [...getSourceItems(viewMode, venues, festivals)];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(query) || item.city?.toLowerCase().includes(query)
      );
    }
    if (selectedRegion) filtered = filtered.filter(item => item.region === selectedRegion);
    if (selectedGenres.length > 0) {
      filtered = filtered.filter(item => {
        if (!item.genres || item.genres.length === 0) return false;
        return genreMatchMode === 'all'
          ? selectedGenres.every(g => item.genres.includes(g))
          : selectedGenres.some(g => item.genres.includes(g));
      });
    }
    if (minCapacity) filtered = filtered.filter(item => (item.capacity || item.estimatedVisitors || 0) >= parseInt(minCapacity));
    if (maxCapacity) filtered = filtered.filter(item => (item.capacity || item.estimatedVisitors || 0) <= parseInt(maxCapacity));

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'relevance': {
          const genresToMatch = selectedGenres.length > 0 ? selectedGenres : (bandProfile?.genres || []);
          const scoreA = calculateGenreMatchScore(a, genresToMatch) + (a.region === bandProfile?.region ? 2 : 0);
          const scoreB = calculateGenreMatchScore(b, genresToMatch) + (b.region === bandProfile?.region ? 2 : 0);
          if (scoreB !== scoreA) return scoreB - scoreA;
          return a.name?.localeCompare(b.name || '') || 0;
        }
        case 'capacity':
          return (b.capacity || b.estimatedVisitors || 0) - (a.capacity || a.estimatedVisitors || 0);
        case 'name':
        default:
          return a.name?.localeCompare(b.name || '') || 0;
      }
    });

    setFilteredItems(filtered);
  }, [venues, festivals, viewMode, searchQuery, selectedRegion, selectedGenres, genreMatchMode, minCapacity, maxCapacity, sortBy, bandProfile]);

  const getUniqueRegions = () => {
    const items = getSourceItems(viewMode, venues, festivals);
    return [...new Set(items.map(v => v.region).filter(Boolean))].sort();
  };

  const getAvailableGenres = () => {
    const items = getSourceItems(viewMode, venues, festivals);
    const genreSet = new Set();
    items.forEach(v => { if (v.genres) v.genres.forEach(g => genreSet.add(g)); });
    return ALL_GENRES.filter(g => genreSet.has(g));
  };

  const toggleGenre = (genre) => {
    setSelectedGenres(prev => prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]);
  };

  const clearFilters = () => {
    setSearchQuery(''); setSelectedRegion(''); setSelectedGenres([]);
    setMinCapacity(''); setMaxCapacity(''); setSortBy('relevance'); setGenreMatchMode('any');
  };

  const hasActiveFilters = searchQuery || selectedRegion || selectedGenres.length > 0 || minCapacity || maxCapacity;
  const venueCount = venues.length;
  const festivalCount = festivals.length;
  const allCount = venueCount + festivalCount;

  // Shared input class
  const selectCls = "w-full bg-transparent border border-surface-border-light rounded-pill py-2 px-3 text-white focus:outline-none focus:border-brand";
  const inputCls = "w-full bg-transparent border border-surface-border-light rounded-pill py-2 px-3 text-white placeholder-text-subtle focus:outline-none focus:border-brand";

  if (loading) {
    return (
      <div className="card-3d rounded-card p-8 text-center">
        <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-text-muted">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Premium Lock Banner */}
      {!isPremium && (
        <div className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-700/50 rounded-card p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="bg-yellow-500/20 p-3 rounded-full">
              <Lock className="w-6 h-6 text-yellow-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-yellow-400 mb-1">Venue Database Locked</h3>
              <p className="text-text-muted text-sm">
                Preview {PREVIEW_COUNT} sample venues below. Upgrade to Premium to access {venues.length + festivals.length}+ venues and festivals with full contact details.
              </p>
            </div>
            <button
              onClick={() => window.location.hash = '#settings'}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-slate-900 font-bold py-2 px-6 transition flex items-center gap-2 whitespace-nowrap rounded-pill"
            >
              <Sparkles className="w-4 h-4" />
              Upgrade
            </button>
          </div>
        </div>
      )}

      {/* Header + Filters */}
      <div className="card-3d rounded-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold mb-2">
              {viewMode === 'venues' && 'Venues Database'}
              {viewMode === 'festivals' && 'Festivals Database'}
              {viewMode === 'all' && 'Venues & Festivals'}
              {!isPremium && ' (Preview)'}
            </h2>
            <p className="text-text-subtle text-sm">
              {isPremium ? (
                <>{filteredItems.length} {filteredItems.length === 1 ? 'result' : 'results'}{hasActiveFilters ? ' (filtered)' : ''}</>
              ) : (
                <>Showing {PREVIEW_COUNT} sample venues</>
              )}
            </p>
          </div>
        </div>

        {isPremium && (
          <>
            {/* View Mode Toggle */}
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { key: 'venues', label: 'Venues', icon: Building, count: venueCount },
                { key: 'festivals', label: 'Festivals', icon: Tent, count: festivalCount },
                { key: 'all', label: 'All', icon: null, count: allCount },
              ].map(({ key, label, icon: Icon, count }) => (
                <button
                  key={key}
                  onClick={() => setViewMode(key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-pill text-sm font-semibold transition ${
                    viewMode === key
                      ? 'bg-brand text-white'
                      : 'bg-white/10 text-text-muted hover:bg-white/20'
                  }`}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {label}
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${viewMode === key ? 'bg-white/20' : 'bg-white/10'}`}>
                    {count}
                  </span>
                </button>
              ))}
            </div>

            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-subtle w-5 h-5" />
                <input
                  type="text"
                  placeholder={`Search ${viewMode === 'all' ? 'venues & festivals' : viewMode}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-transparent border border-surface-border-light rounded-pill py-3 pl-11 pr-4 text-white placeholder-text-subtle focus:outline-none focus:border-brand"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-pill font-semibold transition ${
                  showFilters || hasActiveFilters
                    ? 'bg-brand text-white'
                    : 'bg-white/10 text-text-muted hover:bg-white/20'
                }`}
              >
                <Filter className="w-5 h-5" />
                <span>Filters</span>
                {hasActiveFilters && (
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                    {[searchQuery, selectedRegion, selectedGenres.length > 0, minCapacity, maxCapacity].filter(Boolean).length}
                  </span>
                )}
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-surface-border-light space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-text-subtle mb-1 block">Region</label>
                    <select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)} className={selectCls}>
                      <option value="">All Regions</option>
                      {getUniqueRegions().map(region => <option key={region} value={region}>{region}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-text-subtle mb-1 block">
                      {viewMode === 'festivals' ? 'Min Visitors' : 'Min Capacity'}
                    </label>
                    <input type="number" placeholder="Min" value={minCapacity} onChange={(e) => setMinCapacity(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-text-subtle mb-1 block">
                      {viewMode === 'festivals' ? 'Max Visitors' : 'Max Capacity'}
                    </label>
                    <input type="number" placeholder="Max" value={maxCapacity} onChange={(e) => setMaxCapacity(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-text-subtle mb-1 block">Sort By</label>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={selectCls}>
                      <option value="relevance">Best Match</option>
                      <option value="name">Name (A-Z)</option>
                      <option value="capacity">{viewMode === 'festivals' ? 'Visitors' : 'Capacity'}</option>
                    </select>
                  </div>
                </div>

                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-sm text-brand hover:text-brand/80 transition">
                    Clear all filters
                  </button>
                )}

                {/* Genre Filter */}
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                    <label className="text-xs font-semibold text-text-subtle flex items-center gap-2">
                      <Music className="w-4 h-4" /> Genres
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-text-subtle">Match:</span>
                      {['any', 'all'].map(mode => (
                        <button
                          key={mode}
                          onClick={() => setGenreMatchMode(mode)}
                          className={`text-xs px-2 py-1 rounded-full transition ${
                            genreMatchMode === mode ? 'bg-brand text-white' : 'bg-white/10 text-text-subtle hover:text-white'
                          }`}
                        >
                          {mode.charAt(0).toUpperCase() + mode.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {getAvailableGenres().map(genre => (
                      <button
                        key={genre}
                        onClick={() => toggleGenre(genre)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                          selectedGenres.includes(genre) ? 'bg-brand text-white' : 'bg-white/10 text-text-muted hover:bg-white/20'
                        }`}
                      >
                        {genre}
                      </button>
                    ))}
                  </div>
                  {selectedGenres.length > 0 && (
                    <p className="text-xs text-text-subtle mt-2">
                      {selectedGenres.length} genre{selectedGenres.length !== 1 ? 's' : ''} selected
                      {genreMatchMode === 'all' ? ' (must match all)' : ' (match any)'}
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Items List */}
      <div className="space-y-3">
        {(isPremium ? filteredItems : getPreviewItems(venues)).length === 0 ? (
          <div className="card-3d rounded-card p-8 text-center">
            <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50 text-text-subtle" />
            <p className="text-text-muted mb-2">No {viewMode === 'all' ? 'results' : viewMode} found</p>
            <p className="text-sm text-text-subtle">Try adjusting your filters</p>
          </div>
        ) : (
          (isPremium ? filteredItems : getPreviewItems(venues)).map(item => {
            const isFestival = item.type === 'festival';
            const genreMatches = selectedGenres.length > 0
              ? item.genres?.filter(g => selectedGenres.includes(g)) || []
              : item.genres?.filter(g => bandProfile?.genres?.includes(g)) || [];

            return (
              <div key={item.id} className="card-3d border border-transparent hover:border-brand rounded-card p-4 transition">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  {/* Item Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3 mb-2">
                      <div className={`p-2 rounded-full flex-shrink-0 ${isFestival ? 'bg-orange-500/20' : 'bg-brand/20'}`}>
                        {isFestival ? <Tent className="w-5 h-5 text-orange-400" /> : <Building className="w-5 h-5 text-brand" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-lg">{item.name}</h3>
                          {isFestival && (
                            <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full text-xs font-semibold">Festival</span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-text-subtle">
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.city || 'Unknown'}</span>
                          {item.region && (
                            <>
                              <span className="hidden sm:inline">•</span>
                              <span className="hidden sm:inline">{item.region}</span>
                            </>
                          )}
                          {item.region === bandProfile?.region && (
                            <span className="bg-brand/20 text-brand px-2 py-0.5 rounded-full text-xs font-semibold">Your region</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="flex flex-wrap gap-3 text-sm mb-3">
                      {isFestival ? (
                        <>
                          {item.period && <span className="flex items-center gap-1 text-text-subtle"><Calendar className="w-4 h-4" />{item.period}</span>}
                          {item.estimatedVisitors && <span className="flex items-center gap-1 text-text-subtle"><Users className="w-4 h-4" />{item.estimatedVisitors.toLocaleString()} visitors</span>}
                        </>
                      ) : (
                        item.capacity && <span className="flex items-center gap-1 text-text-subtle"><Users className="w-4 h-4" />Capacity: {item.capacity}</span>
                      )}
                      {genreMatches.length > 0 && (
                        <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full text-xs font-semibold">
                          {genreMatches.length} genre match{genreMatches.length !== 1 ? 'es' : ''}
                        </span>
                      )}
                    </div>

                    {/* Themes (festivals) */}
                    {isFestival && item.themes?.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-1">
                        {item.themes.slice(0, 4).map((theme, idx) => (
                          <span key={idx} className="px-2 py-1 rounded-full text-xs bg-orange-500/20 text-orange-300">{theme}</span>
                        ))}
                        {item.themes.length > 4 && <span className="text-xs text-text-subtle self-center">+{item.themes.length - 4} more</span>}
                      </div>
                    )}

                    {/* Genres */}
                    {item.genres?.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-1">
                        {item.genres.slice(0, 6).map((genre, idx) => {
                          const isMatch = genreMatches.includes(genre);
                          return (
                            <span key={idx} className={`px-2 py-1 rounded-full text-xs ${
                              isMatch ? 'bg-green-500/20 text-green-400 font-semibold' : 'bg-white/10 text-text-muted'
                            }`}>{genre}</span>
                          );
                        })}
                        {item.genres.length > 6 && <span className="text-xs text-text-subtle self-center">+{item.genres.length - 6} more</span>}
                      </div>
                    )}
                  </div>

                  {/* Contact Actions */}
                  <div className="flex sm:flex-col gap-2">
                    {isPremium ? (
                      <>
                        {item.email && (
                          <a href={`mailto:${item.email}`} className="flex items-center gap-2 bg-brand hover:bg-brand-hover transition px-4 py-2 text-sm font-semibold whitespace-nowrap rounded-pill" title={item.email}>
                            <Mail className="w-4 h-4" /><span className="hidden sm:inline">Email</span>
                          </a>
                        )}
                        {item.phone && (
                          <a href={`tel:${item.phone}`} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition px-4 py-2 text-sm font-semibold whitespace-nowrap rounded-pill" title={item.phone}>
                            <Phone className="w-4 h-4" /><span className="hidden sm:inline">Call</span>
                          </a>
                        )}
                        {item.website && (
                          <a href={item.website.startsWith('http') ? item.website : `https://${item.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition px-4 py-2 text-sm font-semibold whitespace-nowrap rounded-pill">
                            <Globe className="w-4 h-4" /><span className="hidden sm:inline">Website</span>
                          </a>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-2 bg-white/5 text-text-subtle px-4 py-2 text-sm font-semibold cursor-not-allowed rounded-pill">
                        <Lock className="w-4 h-4" /><span className="hidden sm:inline">Premium Only</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer Stats */}
      {isPremium ? (
        filteredItems.length > 0 && (
          <div className="card-3d rounded-card p-4 text-center text-sm text-text-subtle">
            Showing {filteredItems.length} of {getSourceItems(viewMode, venues, festivals).length} {viewMode === 'all' ? 'items' : viewMode}
          </div>
        )
      ) : (
        <div className="card-3d rounded-card p-4 text-center text-sm text-text-subtle">
          Showing {PREVIEW_COUNT} preview venues of {venues.length + festivals.length}+ available with Premium
        </div>
      )}
    </div>
  );
}
